import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiRequest } from '@/lib/api'

export interface Project {
  id: string
  ownerId: string
  name: string
  url: string
  domain: string
  extraDomains?: string[]
  apiKey: string
  projectType?: string
  interests?: string[]
  githubRepo?: string | null
  githubAiAuto?: boolean
  jiraCloudId?: string | null
  jiraProjectKey?: string | null
  jiraAiAuto?: boolean
  googleSearchConsoleDomains?: string[]
  geminiApiKeyConfigured?: boolean
  googleGeminiApiKey?: string | null
  openrouterApiKeyConfigured?: boolean
  openrouterApiKey?: string | null
  openrouterApiKeyMasked?: string | null
  // AI shared key & limits
  aiUseSharedKey?: boolean
  aiMonthlySpendLimitCents?: number | null
  aiLimitStop?: boolean
  // Billing fields (optional)
  billingName?: string | null
  billingCountry?: string | null
  billingZip?: string | null
  billingCity?: string | null
  billingStreet?: string | null
  billingVatNumber?: string | null
  // Tracking status
  isTrackingActive?: boolean
  lastTrackingPingAt?: string | null
  createdAt: string
  updatedAt: string
}

export interface CreateProjectData {
  name: string
  url: string
  extraDomains?: string[]
  projectType?: string
  interests?: string[]
  githubRepo?: string
  githubAiAuto?: boolean
  jiraCloudId?: string
  jiraProjectKey?: string
  jiraAiAuto?: boolean
  googleSearchConsoleDomains?: string[]
  googleGeminiApiKey?: string
  openrouterApiKey?: string
  couponCode?: string
}

export interface UpdateProjectData {
  name?: string
  url?: string
  extraDomains?: string[]
  projectType?: string
  interests?: string[]
  githubRepo?: string | null
  githubAiAuto?: boolean
  jiraCloudId?: string | null
  jiraProjectKey?: string | null
  jiraAiAuto?: boolean
  googleSearchConsoleDomains?: string[]
  googleGeminiApiKey?: string | null
  openrouterApiKey?: string | null
  // AI shared billing fields inline with project PUT
  aiUseSharedKey?: boolean
  aiMonthlySpendLimitEur?: number
  // Billing fields
  billingName?: string | null
  billingCountry?: string | null
  billingZip?: string | null
  billingCity?: string | null
  billingStreet?: string | null
  billingVatNumber?: string | null
}

export interface ProjectWithScript extends Project {
  trackingScript: string
}

export interface SubscriptionSummary {
  plan?: 'starter' | 'professional' | 'enterprise'
  status?: 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'unpaid'
  trialStartedAt?: string
  trialEndsAt?: string | null
  cancelAt?: string | null
  dailySessionLimit?: number | null
  dailySessionCount?: number
  dailySessionDate?: string
  stripeCustomerId?: string | null
  stripeSubscriptionId?: string | null
  stripeProductId?: string | null
  stripePriceId?: string | null
  unitAmountCents?: number | null
  currency?: string | null
  scheduledDowngradeAt?: string | null
  scheduledPlan?: 'starter' | 'professional' | 'enterprise' | null
  nextBillingAt?: string | null
  paymentProblem?: boolean
  paymentActionRequired?: boolean
  paymentUrl?: string | null
  amountDueCents?: number | null
  pendingUpdate?: boolean
  hasScheduledChange?: boolean
  lastSyncedAt?: string | null
  syncWarning?: boolean
}

export interface PlanInfo {
  plan: 'starter' | 'professional' | 'enterprise'
  productId: string
  priceId: string
  unitAmount: number | null
  currency: string
  dailyLimit: number | null
}

export const useProjectsStore = defineStore('projects', () => {
  // State
  const projects = ref<Project[]>([])
  const currentProject = ref<ProjectWithScript | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const subscriptionCache = ref<Record<string, SubscriptionSummary>>({})
  const plans = ref<PlanInfo[] | null>(null)

  // Computed
  const hasProjects = computed(() => projects.value.length > 0)
  const projectsCount = computed(() => projects.value.length)
  const plansByName = computed<Record<string, PlanInfo>>(() => {
    const map: Record<string, PlanInfo> = {}
    for (const p of plans.value || []) map[p.plan] = p
    return map
  })

  // Actions
  const clearError = () => {
    error.value = null
  }

  const fetchProjects = async () => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiRequest<Project[]>('/projects', {
        method: 'GET'
      })

      if (!response.success) {
        throw new Error(response.message)
      }

      projects.value = response.data || []
      return response
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch projects'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const createProject = async (projectData: CreateProjectData) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiRequest<{ project: Project; trackingScript: string }>('/projects', {
        method: 'POST',
        body: JSON.stringify(projectData)
      })

      if (!response.success) {
        throw new Error(response.message)
      }

      // Add the new project to the list
      if (response.data?.project) {
        projects.value.unshift(response.data.project)
        // Set as current project with script
        currentProject.value = {
          ...response.data.project,
          trackingScript: response.data.trackingScript
        }
      }

      return response
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create project'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const getProject = async (projectId: string) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiRequest<{ project: Project; trackingScript: string }>(`/projects/${projectId}`, {
        method: 'GET'
      })

      if (!response.success) {
        throw new Error(response.message)
      }

      if (response.data) {
        currentProject.value = {
          ...response.data.project,
          trackingScript: response.data.trackingScript
        }
      }

      return response
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch project'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const updateProject = async (projectId: string, projectData: UpdateProjectData) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiRequest<{ project: Project; trackingScript: string }>(`/projects/${projectId}`, {
        method: 'PUT',
        body: JSON.stringify(projectData)
      })

      if (!response.success) {
        throw new Error(response.message)
      }

      if (response.data?.project) {
        // Update in the projects list
        const index = projects.value.findIndex((p: Project) => p.id === projectId)
        if (index !== -1) {
          projects.value[index] = response.data.project
        }

        // Update current project if it's the same one
        if (currentProject.value?.id === projectId) {
          currentProject.value = {
            ...response.data.project,
            trackingScript: response.data.trackingScript
          }
        }
      }

      return response
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update project'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const deleteProject = async (projectId: string) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiRequest(`/projects/${projectId}`, {
        method: 'DELETE'
      })

      if (!response.success) {
        throw new Error(response.message)
      }

      // Remove from projects list
      projects.value = projects.value.filter((p: Project) => p.id !== projectId)

      // Clear current project if it was deleted
      if (currentProject.value?.id === projectId) {
        currentProject.value = null
      }

      return response
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete project'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const regenerateApiKey = async (projectId: string) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await apiRequest<{ project: Project; trackingScript: string }>(`/projects/${projectId}/regenerate-api-key`, {
        method: 'POST'
      })

      if (!response.success) {
        throw new Error(response.message)
      }

      if (response.data?.project) {
        // Update in the projects list
        const index = projects.value.findIndex((p: Project) => p.id === projectId)
        if (index !== -1) {
          projects.value[index] = response.data.project
        }

        // Update current project if it's the same one
        if (currentProject.value?.id === projectId) {
          currentProject.value = {
            ...response.data.project,
            trackingScript: response.data.trackingScript
          }
        }
      }

      return response
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to regenerate API key'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // AI settings & usage
  const updateAiSettings = async (projectId: string, payload: { useSharedKey?: boolean; monthlyTokenLimit?: number; monthlySpendLimitEur?: number }) => {
    return apiRequest(`/projects/${projectId}/settings/ai`, { method: 'PUT', body: JSON.stringify(payload) })
  }
  const fetchAiUsage = async (
    projectId: string,
    params?: { range?: '7d' | '30d' | '90d' | 'month' | 'custom'; from?: string; to?: string }
  ) => {
    const qp = new URLSearchParams()
    if (params?.range) qp.set('range', params.range)
    if (params?.from) qp.set('from', params.from)
    if (params?.to) qp.set('to', params.to)
    const suffix = qp.toString() ? `?${qp.toString()}` : ''
    return apiRequest(`/projects/${projectId}/ai/usage${suffix}`, { method: 'GET' })
  }
  const setupAiBilling = async (projectId: string) => {
    return apiRequest<{ url: string }>(`/projects/${projectId}/ai/setup-billing`, { method: 'POST' })
  }
  const enableSharedAi = async (projectId: string) => {
    return apiRequest(`/projects/${projectId}/ai/enable-shared`, { method: 'POST' })
  }
  const fetchAiPaymentStatus = async (projectId: string) => {
    return apiRequest<{ hasCard: boolean }>(`/projects/${projectId}/ai/payment-status`, { method: 'GET' })
  }

  // Members & invitations
  const listMembers = async (projectId: string) => {
    return apiRequest(`/projects/${projectId}/members`, { method: 'GET' })
  }
  const inviteTeammate = async (projectId: string, payload: { email: string }) => {
    return apiRequest(`/projects/${projectId}/invitations`, { method: 'POST', body: JSON.stringify({ email: payload.email }) })
  }
  const acceptInvitation = async (token: string) => {
    return apiRequest(`/projects/invitations/${token}/accept`, { method: 'POST' })
  }
  const declineInvitation = async (token: string) => {
    return apiRequest(`/projects/invitations/${token}/decline`, { method: 'POST' })
  }
  const removeMember = async (projectId: string, userId: string) => {
    return apiRequest(`/projects/${projectId}/members/${userId}`, { method: 'DELETE' })
  }
  const revokeInvitation = async (projectId: string, inviteId: string) => {
    return apiRequest(`/projects/${projectId}/invitations/${inviteId}`, { method: 'DELETE' })
  }
  const transferOwnership = async (projectId: string, userId: string) => {
    return apiRequest(`/projects/${projectId}/transfer-ownership`, { method: 'POST', body: JSON.stringify({ userId }) })
  }

  const clearCurrentProject = () => {
    currentProject.value = null
  }

  const findProjectById = (projectId: string): Project | undefined => {
    return projects.value.find((p: Project) => p.id === projectId)
  }
  const findProjectByDomain = (domain: string): Project | undefined => {
    return projects.value.find((p: Project) => p.domain === domain)
  }

  // Trial state helper - returns isExpired, isEnding (<=3 days), and daysLeft
  const getTrialState = (projectId: string): { isExpired: boolean; isEnding: boolean; daysLeft: number | null } => {
    const sub = subscriptionCache.value[projectId]
    if (!sub) return { isExpired: false, isEnding: false, daysLeft: null }
    
    const status = (sub.status || '').toLowerCase()
    
    // Only check trial state if status is 'trialing'
    if (status !== 'trialing') {
      return { isExpired: false, isEnding: false, daysLeft: null }
    }
    
    if (!sub.trialEndsAt) {
      return { isExpired: false, isEnding: false, daysLeft: null }
    }
    
    try {
      const trialEnd = new Date(sub.trialEndsAt).getTime()
      if (!Number.isFinite(trialEnd)) {
        return { isExpired: false, isEnding: false, daysLeft: null }
      }
      
      const now = Date.now()
      const diffMs = trialEnd - now
      const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
      
      if (daysLeft <= 0) {
        return { isExpired: true, isEnding: false, daysLeft: 0 }
      }
      
      if (daysLeft <= 3) {
        return { isExpired: false, isEnding: true, daysLeft }
      }
      
      return { isExpired: false, isEnding: false, daysLeft }
    } catch {
      return { isExpired: false, isEnding: false, daysLeft: null }
    }
  }

  return {
    // State
    projects,
    currentProject,
    isLoading,
    error,
    subscriptionCache,
    plans,

    // Computed
    hasProjects,
    projectsCount,
    plansByName,

    // Actions
    clearError,
    fetchProjects,
    createProject,
    getProject,
    updateProject,
    deleteProject,
    regenerateApiKey,
    clearCurrentProject,
    findProjectById,
    findProjectByDomain,
    getTrialState,
    listMembers,
    inviteTeammate,
    acceptInvitation,
    removeMember,
    revokeInvitation,
    transferOwnership,
    declineInvitation,
    // Privacy
    async fetchPrivacySettings(projectId: string) {
      return apiRequest(`/projects/${projectId}/settings/privacy`, { method: 'GET' })
    },
    async updatePrivacySettings(projectId: string, payload: { respectPrivacySignals?: boolean; ip?: { strategy?: 'truncate' | 'hash'; v4Mask?: number; v6Mask?: number }; storeUserAgent?: boolean; geocode?: boolean }) {
      return apiRequest(`/projects/${projectId}/settings/privacy`, { method: 'PUT', body: payload as any })
    },
    // Billing
    async fetchSubscription(projectId: string) {
      const res = await apiRequest<SubscriptionSummary>(`/projects/${projectId}/subscription`, { method: 'GET' })
      if (res.success && res.data) {
        subscriptionCache.value[projectId] = res.data
      }
      return res
    },
    async fetchPlans() {
      const res = await apiRequest<PlanInfo[]>(`/billing/plans`, { method: 'GET' })
      if (res.success && Array.isArray(res.data)) {
        plans.value = res.data
      }
      return res
    },
    async createCheckout(projectId: string, plan: 'starter' | 'professional' | 'enterprise') {
      const res = await apiRequest<{ url: string }>(`/projects/${projectId}/billing/checkout`, { method: 'POST', body: JSON.stringify({ plan }) })
      return res
    },
    async openBillingPortal(projectId: string) {
      const res = await apiRequest<{ url: string }>(`/projects/${projectId}/billing/portal`, { method: 'POST' })
      return res
    },
    async changePlan(projectId: string, plan: 'starter' | 'professional' | 'enterprise') {
      return apiRequest(`/projects/${projectId}/billing/change-plan`, { method: 'POST', body: JSON.stringify({ plan }) })
    },
    async cancelSubscription(projectId: string) {
      return apiRequest<{ subscriptionId: string; cancelAt: number }>(`/projects/${projectId}/billing/cancel`, { method: 'POST' })
    },
    // AI
    updateAiSettings,
    fetchAiUsage,
    setupAiBilling,
    enableSharedAi,
    fetchAiPaymentStatus,
  }
})
